import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { c204Api, FC120, FC121 } from '../../../services/c204Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { useNavigate } from 'react-router-dom';

export type Mode = 'browse' | 'add' | 'edit';

const INVOICE_OPTIONS = [
  { value: 1, label: '三聯式' },
  { value: 2, label: '二聯式' },
  { value: 3, label: '外銷發票-零稅率' },
  { value: 4, label: 'DN-內' },
  { value: 5, label: 'DN-外' },
];

const DELIVERY_TYPE_OPTIONS = [
  { value: 1, label: '正常' },
  { value: 2, label: '異常' },
];

export function useDeliveryForm(reviewNo?: string | null) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FC120[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<FC120>>({});
  const [details, setDetails] = useState<Partial<FC121>[]>([]);
  const [lookupNames, setLookupNames] = useState<Record<string, string>>({});
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load records list
  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await c204Api.list(1, 500, keyword);
      setLastApiResponse(res);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Resolve lookup names for display
  const resolveNames = useCallback(async (data: Partial<FC120>) => {
    const entries: [string, string, string][] = [
      ['customer', 'customer', data.fc120_12 || ''],
      ['department', 'department', data.fc120_17 || ''],
      ['warehouse', 'warehouse', data.fc120_14 || ''],
      ['receiver', 'customer', data.fc120_21 || ''],
      ['consignWarehouse', 'warehouse', data.fc120_44 || ''],
      ['entryPerson', 'employee', data.fc120_94 || ''],
      ['salesperson', 'employee', data.fc120_91 || ''],
    ];
    const results = await Promise.all(
      entries.map(([key, type, code]) =>
        c204Api.lookupName(type, code).then(name => [key, name] as [string, string])
      )
    );
    setLookupNames(prev => {
      const next = { ...prev };
      for (const [key, name] of results) next[key] = name;
      return next;
    });
  }, []);

  const resolveSingleName = useCallback(async (nameKey: string, type: string, code: string) => {
    const name = await c204Api.lookupName(type, code);
    setLookupNames(prev => ({ ...prev, [nameKey]: name }));
  }, []);

  // Sync formData when current changes in browse mode
  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData({ ...current });
      setDetails(current.details || []);
      resolveNames(current);
    }
  }, [current, mode, resolveNames]);

  // Initial load — if reviewNo provided, navigate to that record
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length === 0) return;
      if (reviewNo) {
        const idx = data.findIndex(r => r.fc120_01 === reviewNo);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else {
        setCurrentIdx(0);
      }
    });
  }, [loadRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = useCallback(() => {
    clearFieldErrors();
    setCurrentIdx(-1);
    setMode('add');
    const today = new Date().toISOString();
    setFormData({
      fc120_11: today,  // 交運日期預設今天
      fc120_32: today,  // 打單日期預設今天
      fc120_13: 1,
      fc120_19: 1,
      fc120_291: 1,
      fc120_80: '',
    });
    setDetails([]);
    setLookupNames({});
  }, [clearFieldErrors]);

  // Start edit mode
  const startEdit = useCallback(() => {
    if (!current) return;
    if (current.fc120_80 === 'P' || current.fc120_80 === 'C') {
      message.warning('已核准或已取消的單據不可修改');
      return;
    }
    setMode('edit');
    setFormData({ ...current });
    setDetails(current.details || []);
  }, [current]);

  // Cancel editing
  const cancel = useCallback(() => {
    clearFieldErrors();
    setMode('browse');
    if (current) {
      setFormData({ ...current });
      setDetails(current.details || []);
    }
  }, [current, clearFieldErrors]);

  // Save (create or update)
  const save = useCallback(async () => {
    try {
      setLoading(true);
      const req = {
        master: formData as FC120,
        details: details as FC121[],
      };
      if (mode === 'add') {
        const created = await c204Api.create(req);
        setLastApiResponse(created);
        message.success(`新增成功: ${created.fc120_01}`);
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fc120_01 === created.fc120_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else if (current) {
        const updated = await c204Api.update(current.fc120_01, req);
        setLastApiResponse(updated);
        message.success('修改成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fc120_01 === updated.fc120_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map((e: { message: string }) => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, mode, current, loadRecords, setFieldErrors]);

  // Delete
  const doDelete = useCallback(async () => {
    if (!current) return;
    try {
      await c204Api.delete(current.fc120_01);
      setLastApiResponse({ deleted: current.fc120_01 });
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  }, [current, loadRecords, currentIdx]);

  // Submit for approval
  const submitOrder = useCallback(async () => {
    if (!current) return;
    try {
      const response = await c204Api.updateStatus(current.fc120_01, 'D');
      setLastApiResponse(response);
      message.success('送審成功');
      await loadRecords();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '送審失敗');
    }
  }, [current, loadRecords]);

  // Submit for approval (new pattern — dedicated endpoint)
  const handleSubmit = useCallback(async () => {
    if (!current) return;
    try {
      const response = await c204Api.submit(current.fc120_01);
      setLastApiResponse(response);
      message.success('送審成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.fc120_01 === current.fc120_01);
      if (idx >= 0) setCurrentIdx(idx);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '送審失敗');
    }
  }, [current, loadRecords]);

  // Approve (new pattern — dedicated endpoint)
  const handleApprove = useCallback(async (): Promise<boolean> => {
    if (!current) return false;
    try {
      const response = await c204Api.approve(current.fc120_01);
      setLastApiResponse(response);
      message.success('核准成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.fc120_01 === current.fc120_01);
      if (idx >= 0) setCurrentIdx(idx);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准失敗');
      return false;
    }
  }, [current, loadRecords]);

  // Reject (new pattern — dedicated endpoint)
  const handleReject = useCallback(async (): Promise<boolean> => {
    if (!current) return false;
    try {
      const response = await c204Api.reject(current.fc120_01);
      setLastApiResponse(response);
      message.success('退回成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.fc120_01 === current.fc120_01);
      if (idx >= 0) setCurrentIdx(idx);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '退回失敗');
      return false;
    }
  }, [current, loadRecords]);

  // Withdraw approval
  const withdrawOrder = useCallback(async () => {
    if (!current) return;
    try {
      const response = await c204Api.updateStatus(current.fc120_01, 'N');
      setLastApiResponse(response);
      message.success('取回成功');
      await loadRecords();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '取回失敗');
    }
  }, [current, loadRecords]);

  // Update a field
  const updateField = useCallback((field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Update a detail row field
  const updateDetailField = useCallback((rowIdx: number, field: string, value: unknown) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        next[rowIdx] = { ...next[rowIdx], [field]: value };
      }
      return next;
    });
  }, []);

  // Add detail row
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fc121_02 || 0), 0);
    setDetails(prev => [...prev, { fc121_02: maxSeq + 1, fc121_01: formData.fc120_01 || '' }]);
  }, [details, formData.fc120_01]);

  // Delete detail row
  const deleteDetailRow = useCallback((rowIdx: number) => {
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== rowIdx);
      return next.map((d, i) => ({ ...d, fc121_02: i + 1 }));
    });
  }, []);

  // Status label
  const statusLabel = useCallback((status: string): string => {
    switch (status) {
      case 'D': return '送審中';
      case 'P': return '已核准';
      case 'C': return '已取消';
      default: return '草稿';
    }
  }, []);

  // Computed: NTD total with tax
  const ntdTotalWithTax = ((formData.fc120_20 || 0) + (formData.fc120_16 || 0)) * (formData.fc120_19 || 1);

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    formData, details, lookupNames,
    updateField, updateDetailField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, submitOrder, withdrawOrder,
    handleSubmit, handleApprove, handleReject,
    loadRecords, statusLabel,
    setDetails,
    addDetailRow, deleteDetailRow,
    resolveSingleName,
    ntdTotalWithTax,
    INVOICE_OPTIONS, DELIVERY_TYPE_OPTIONS,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
    navigate,
    lastApiResponse,
  };
}
